import { motion, useScroll, useTransform } from "framer-motion";
import { useLenis } from "lenis/react";
import { ArrowDown, Star } from "lucide-react";

const EASE = [0.16, 1, 0.3, 1];

const Line = ({ children, delay = 0, className = "" }) => (
  <span className={`block overflow-hidden ${className}`}>
    <motion.span
      className="block will-change-transform"
      initial={{ y: "115%" }}
      animate={{ y: "0%" }}
      transition={{ duration: 1.2, delay, ease: EASE }}
    >
      {children}
    </motion.span>
  </span>
);

export default function Hero() {
  const lenis = useLenis();
  const { scrollY } = useScroll();
  const yBg = useTransform(scrollY, [0, 900], [0, 240]);
  const yPizza = useTransform(scrollY, [0, 900], [0, 180]);
  const rotate = useTransform(scrollY, [0, 1400], [0, 130]);
  const fade = useTransform(scrollY, [0, 500], [1, 0]);

  return (
    <section id="top" data-testid="hero-section" className="relative h-[100svh] min-h-[640px] overflow-hidden">
      <motion.div style={{ y: yBg }} className="absolute inset-0 scale-110">
        <img
          src="https://images.unsplash.com/photo-1677175245494-dc306351b541?ixlib=rb-4.1.0&q=85&fm=jpg&crop=entropy&cs=srgb&w=2000"
          alt="Pizza artigianale"
          className="w-full h-full object-cover"
        />
      </motion.div>
      <div className="absolute inset-0 bg-gradient-to-b from-ink/80 via-ink/55 to-ink" />
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_70%_60%_at_50%_40%,transparent_0%,rgba(10,10,10,0.55)_100%)]" />

      <motion.div
        style={{ y: yPizza, rotate }}
        className="absolute right-[-8%] bottom-[-6%] sm:right-[2%] sm:bottom-[4%] w-[62vw] sm:w-[38vw] max-w-[440px] z-10 pointer-events-none"
      >
        <div className="spotlight absolute inset-[-20%] rounded-full" />
        <motion.img
          src="/assets/pizza-tartufo.png"
          alt="Pizza Tartufo Gold di Grano"
          data-testid="hero-pizza-image"
          className="relative w-full drop-shadow-[0_40px_60px_rgba(0,0,0,0.6)]"
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1.4, delay: 0.9, ease: EASE }}
        />
      </motion.div>

      <motion.div style={{ opacity: fade }} className="relative z-20 h-full max-w-7xl mx-auto px-6 flex flex-col justify-center">
        <Line delay={0.15}>
          <span data-testid="hero-eyebrow" className="text-[11px] sm:text-xs tracking-[0.45em] uppercase text-gold">
            Pizzeria Artigianale · Fiumefreddo di Sicilia
          </span>
        </Line>
        <h1 className="font-display leading-[0.82] mt-6">
          <Line delay={0.3}>
            <span data-testid="hero-title-1" className="text-[24vw] sm:text-[19vw] lg:text-[15rem] tracking-tighter text-cream">
              GRANO
            </span>
          </Line>
          <Line delay={0.45}>
            <span data-testid="hero-title-2" className="block text-[7.5vw] sm:text-[5vw] lg:text-[3.8rem] italic font-light text-cream/85 mt-2">
              dal chicco alla tavola
            </span>
          </Line>
        </h1>
        <Line delay={0.65} className="mt-8 max-w-xl">
          <p data-testid="hero-subtitle" className="text-sm sm:text-base text-cream/60 leading-relaxed">
            Farine siciliane di Russello, Timilia e Perciasacchi. Lievitazione lenta
            24/48 ore. Pizze che raccontano la Sicilia, una fetta alla volta.
          </p>
        </Line>
        <Line delay={0.8}>
          <div className="flex flex-wrap items-center gap-4 mt-10">
            <button
              data-testid="hero-cta-menu"
              onClick={() => lenis?.scrollTo("#menu", { offset: -60, duration: 1.6 })}
              className="bg-terra text-cream text-xs tracking-[0.25em] uppercase px-8 py-4 hover:bg-cream hover:text-ink transition-colors duration-300"
            >
              Scopri il Menù
            </button>
            <a
              data-testid="hero-cta-call"
              href="tel:+393892472195"
              className="border border-cream/30 text-cream text-xs tracking-[0.25em] uppercase px-8 py-4 hover:border-gold hover:text-gold transition-colors duration-300"
            >
              Chiama Ora
            </a>
            <div data-testid="hero-rating" className="flex items-center gap-2 text-cream/60 text-xs tracking-[0.15em] uppercase sm:ml-4">
              <Star size={13} className="text-gold fill-gold" />
              4.6 su Google · 8.9 su TheFork
            </div>
          </div>
        </Line>
      </motion.div>

      <motion.div
        style={{ opacity: fade }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 z-20 flex flex-col items-center gap-2 text-cream/40"
      >
        <span className="text-[10px] tracking-[0.4em] uppercase">Scorri</span>
        <motion.div animate={{ y: [0, 8, 0] }} transition={{ duration: 1.8, repeat: Infinity, ease: "easeInOut" }}>
          <ArrowDown size={16} strokeWidth={1.5} />
        </motion.div>
      </motion.div>
    </section>
  );
}
